/**
 * Workforce Schedule — Phase 2 of the Enterprise Workforce Suite.
 *
 * Reads (never writes) the existing Appointments module. Displays a
 * week-view swim-lane calendar for one of three resource types:
 *   * Therapist — shows every therapist as a lane, filled from both
 *     Appointments (attendee_user_ids) AND existing Sessions.
 *   * Room — one lane per Room (Rooms are managed here inline).
 *   * Equipment — one lane per Equipment item.
 *
 * The user picks the week with a next/prev arrow. Holidays for the same
 * range are overlaid as a coloured band at the top of the calendar.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Calendar, ArrowCircleLeft, ArrowCircleRight, House, Package, UsersFour, Gear,
} from "@phosphor-icons/react";
import ResourceManagerDialog from "@/components/workforce/ResourceManagerDialog";

const RESOURCES = [
  { key: "therapist", label: "Therapist",  icon: UsersFour },
  { key: "room",      label: "Room",       icon: House },
  { key: "equipment", label: "Equipment",  icon: Package },
];


/* Day/time math helpers — kept local, not exported, to avoid pulling in
   a date lib. All dates use the browser's local zone; the API accepts
   ISO strings so we compose them from the Date object directly. */
function startOfWeek(d) {
  const x = new Date(d);
  const dow = x.getDay();
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoStr(d) { return d.toISOString(); }
function fmtDay(d) { return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }


export default function ScheduleView() {
  const { branches, activeBranch } = useAuth();
  const [resource, setResource] = useState("therapist");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [branchFilter, setBranchFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [managerOpen, setManagerOpen] = useState(null); // "room" | "equipment" | null

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        resource,
        from_at: isoStr(weekStart),
        to_at: isoStr(weekEnd),
      };
      if (branchFilter !== "all") params.branch_id = branchFilter;
      const { data } = await api.get("/workforce/schedule", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [resource, weekStart, weekEnd, branchFilter]);
  useEffect(() => { load(); }, [load]);

  // Group each lane's events by day-of-week for the swim-lane grid.
  const lanesByDay = useMemo(() => {
    if (!data) return [];
    return data.lanes.map(lane => {
      const buckets = Array.from({ length: 7 }, () => []);
      for (const ev of lane.events) {
        const d = new Date(ev.start_at);
        const dayIdx = Math.floor((d - weekStart) / (24 * 60 * 60 * 1000));
        if (dayIdx >= 0 && dayIdx < 7) buckets[dayIdx].push(ev);
      }
      return { ...lane, buckets };
    });
  }, [data, weekStart]);

  const holidaysByDay = useMemo(() => {
    if (!data) return Array.from({ length: 7 }, () => []);
    const buckets = Array.from({ length: 7 }, () => []);
    for (const h of data.holidays) {
      const d = new Date(h.date + "T00:00:00");
      const dayIdx = Math.floor((d - weekStart) / (24 * 60 * 60 * 1000));
      if (dayIdx >= 0 && dayIdx < 7) buckets[dayIdx].push(h);
    }
    return buckets;
  }, [data, weekStart]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="schedule-view">
      <div className="flex items-center gap-3 flex-wrap">
        <Calendar size={28} className="text-primary" weight="duotone" />
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Workforce schedule</h1>
          <p className="text-xs text-muted-foreground">
            Read-only view of appointments and sessions — grouped by resource. Edit calendar events in the Appointments module.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="schedule-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(branches || []).map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {resource !== "therapist" && (
            <Button variant="outline" onClick={() => setManagerOpen(resource)} data-testid={`manage-${resource}`} className="gap-1.5">
              <Gear size={13} /> Manage {resource}s
            </Button>
          )}
        </div>
      </div>

      <Tabs value={resource} onValueChange={setResource}>
        <TabsList data-testid="schedule-tabs">
          {RESOURCES.map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key} data-testid={`tab-${key}`}>
              <Icon size={13} className="mr-1.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {RESOURCES.map(r => (
          <TabsContent key={r.key} value={r.key} className="mt-4 space-y-3">
            {/* Week nav + summary */}
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setWeekStart(addDays(weekStart, -7))} className="gap-1" data-testid="week-prev">
                <ArrowCircleLeft size={14} weight="fill" /> Previous
              </Button>
              <div className="text-sm font-medium tabular-nums">
                {fmtDay(weekStart)} — {fmtDay(addDays(weekEnd, -1))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setWeekStart(addDays(weekStart, 7))} className="gap-1" data-testid="week-next">
                Next <ArrowCircleRight size={14} weight="fill" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} className="ml-auto" data-testid="week-today">
                Today
              </Button>
            </div>

            {/* Grid */}
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
                {!loading && (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium w-40 sticky left-0 bg-muted/40 border-r">{r.label}</th>
                        {days.map((d, i) => (
                          <th key={d.toISOString().slice(0, 10)} className="text-left px-2 py-2 font-medium border-r border-border/50 min-w-[140px]" data-testid={`day-header-${i}`}>
                            <div>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                            <div className="text-[10px] text-muted-foreground tabular-nums">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                            {holidaysByDay[i].map(h => (
                              <div
                                key={h.id}
                                className="mt-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium text-white truncate"
                                style={{ background: h.color }}
                                title={`${h.name} (${h.type}${h.is_working_day ? " · working day" : ""})`}
                                data-testid={`holiday-${h.id}`}
                              >
                                {h.name}
                              </div>
                            ))}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lanesByDay.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted-foreground py-8">
                            {resource === "therapist"
                              ? "No therapists in this workspace yet. Add users with the therapist role first."
                              : `No ${r.label.toLowerCase()}s yet. Use "Manage ${r.label.toLowerCase()}s" to add some.`}
                          </td>
                        </tr>
                      )}
                      {lanesByDay.map(lane => (
                        <tr key={lane.id} className="border-t" data-testid={`lane-${lane.id}`}>
                          <td className="px-3 py-2 sticky left-0 bg-background border-r">
                            <div className="flex items-center gap-2">
                              {lane.color && <span className="w-2.5 h-2.5 rounded-full" style={{ background: lane.color }} />}
                              <div className="font-medium truncate">{lane.name}</div>
                            </div>
                            <div className="text-[10px] text-muted-foreground">{lane.events.length} events</div>
                          </td>
                          {lane.buckets.map((events, di) => (
                            <td key={`${lane.id}-day-${di}`} className="align-top px-1.5 py-1.5 border-r border-border/50 h-20 relative">
                              <div className="space-y-1">
                                {events.map(ev => (
                                  <EventChip key={ev.id + ev.source} ev={ev} laneColor={lane.color} />
                                ))}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <ResourceManagerDialog
        open={!!managerOpen}
        resource={managerOpen}
        onClose={() => setManagerOpen(null)}
        onChanged={load}
        branches={branches || []}
      />
    </div>
  );
}


function EventChip({ ev, laneColor }) {
  const bg = laneColor || (ev.source === "session" ? "#4A6753" : "#4B5563");
  const start = new Date(ev.start_at);
  const end = new Date(ev.end_at);
  const timeRange = `${start.getHours().toString().padStart(2, "0")}:${start.getMinutes().toString().padStart(2, "0")}–${end.getHours().toString().padStart(2, "0")}:${end.getMinutes().toString().padStart(2, "0")}`;
  return (
    <div
      className="rounded px-1.5 py-1 text-white text-[10px] leading-tight cursor-default hover:opacity-90"
      style={{ background: bg, opacity: ev.status === "cancelled" ? 0.35 : 0.92 }}
      title={`${ev.title} · ${timeRange}${ev.location ? " · " + ev.location : ""}`}
      data-testid={`event-${ev.id}`}
    >
      <div className="truncate font-medium">{ev.title}</div>
      <div className="opacity-80 tabular-nums">{timeRange}</div>
    </div>
  );
}
